// The Books module's pages. The host mounts this beneath its `ModuleGate` (which decides absent /
// off / setting up / locked first), on the route that names the page. Every page reads the HOME
// first — identity, period ladder, statement catalog, chart and the words its codes are said in —
// and no page branches on what kind of organization the books belong to.

import type { ReactNode } from 'react';
import { Button, testIdProp } from '@givmo/design-system';
import type { BooksConfig } from './books-config';
import {
  BOOKS_EXPORT_PERMISSION,
  BOOKS_PAGE_TEST_ID,
  BOOKS_READ_PERMISSION,
  type BooksView,
} from './books-routes';
import { BOOKS_REFUSAL_COPY, booksRefusal } from './books-state';
import { booksWords } from './books-words';
import { useBooksHome } from './books-queries';
import { BooksPageShell, BooksStateCard } from './books-ui';
import { HomePage } from './home-page';
import { StatementsPage } from './statements-page';
import { LedgerPage } from './ledger-page';
import { AccountPage } from './account-page';
import { ReconciliationPage } from './reconciliation-page';
import { ExportPage } from './export-page';

const PAGE_TITLES: Record<BooksView['page'], string> = {
  overview: 'Overview',
  statements: 'Statements',
  ledger: 'Ledger',
  journal: 'Ledger',
  account: 'Ledger',
  reconciliation: 'Reconciliation',
  export: 'Export',
};

function holds(permissions: readonly string[] | null, permission: string): boolean {
  return permissions === null || permissions.includes(permission);
}

export function BooksPage({
  config,
  view = { page: 'overview' },
}: {
  config: BooksConfig;
  view?: BooksView;
}) {
  const mayRead = holds(config.permissions, BOOKS_READ_PERMISSION);
  const home = useBooksHome(mayRead ? config.orgRef : null);
  const title = PAGE_TITLES[view.page];

  let page: ReactNode;
  if (!mayRead) {
    page = (
      <BooksPageShell title="Books" testId="books-locked">
        <BooksStateCard testId="books-no-access" title="You don’t have access to Books data" />
      </BooksPageShell>
    );
  } else if (config.orgRef === null || home.isPending) {
    page = (
      <BooksPageShell title={title} testId="books-waiting">
        <p
          role="status"
          className="font-sans text-s13 text-text-secondary"
          {...testIdProp('books-loading')}
        >
          Loading Books…
        </p>
      </BooksPageShell>
    );
  } else if (home.isError) {
    const refusal = booksRefusal(home.error);
    const copy = BOOKS_REFUSAL_COPY[refusal];
    page = (
      <BooksPageShell title={title} testId="books-refused">
        <BooksStateCard
          testId={`books-${refusal}`}
          title={copy.title}
          description={copy.description}
          action={
            copy.retry ? (
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                className="max-sm:h-44"
                testId="books-retry"
                onClick={() => void home.refetch()}
              >
                Try again
              </Button>
            ) : undefined
          }
        />
      </BooksPageShell>
    );
  } else if (home.data === null) {
    // A success this build cannot read: say so, and show nothing from it.
    page = (
      <BooksPageShell title={title} testId="books-unreadable">
        <BooksStateCard
          testId="books-unsupported"
          title="Books can’t be shown right now"
          description="Please try again later."
        />
      </BooksPageShell>
    );
  } else {
    const data = home.data;
    const words = booksWords(data.vocabulary);
    switch (view.page) {
      case 'overview':
        page = <HomePage home={data} words={words} />;
        break;
      case 'statements':
        page = <StatementsPage home={data} words={words} statementId={view.statement} />;
        break;
      case 'ledger':
        page = <LedgerPage home={data} words={words} tab="accounts" />;
        break;
      case 'journal':
        page = <LedgerPage home={data} words={words} tab="journal" />;
        break;
      case 'account':
        page = <AccountPage home={data} words={words} accountCode={view.accountCode} />;
        break;
      case 'reconciliation':
        page = <ReconciliationPage home={data} words={words} />;
        break;
      case 'export':
        page = (
          <ExportPage home={data} mayExport={holds(config.permissions, BOOKS_EXPORT_PERMISSION)} />
        );
        break;
    }
  }
  return <div {...testIdProp(BOOKS_PAGE_TEST_ID)}>{page}</div>;
}
